import { ArrowLeft, Shield } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_soulcontent-1/artifacts/eh8zi2sq_logo.svg";

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="privacy-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3" data-testid="privacy-nav-logo">
              <img src={LOGO_URL} alt="GospelTube" className="h-10 w-auto" />
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="privacy-back-btn"
            >
              <ArrowLeft size={20} />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="p-3 rounded-xl bg-brand/20 text-brand">
              <Shield size={28} />
            </div>
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-white">Privacy Policy</h1>
              <p className="text-gray-500 text-sm">Effective Date: January 30, 2026 | Last Updated: January 30, 2026</p>
            </div>
          </div>

          <div className="prose prose-invert prose-lg max-w-none">
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-8 md:p-12 space-y-8">
              
              <p className="text-gray-400 leading-relaxed">
                This Privacy Policy explains how GospelTube Inc ("GospelTube," "we," "us," or "our") collects, uses, shares, and protects information when you use our Service on iOS, Android, web browsers, and smart TVs.
              </p>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">1) Information We Collect</h2>
                
                <h3 className="text-xl font-medium text-white mt-6 mb-3">a) Information you provide</h3>
                <ul className="list-disc list-inside text-gray-400 space-y-2">
                  <li><strong className="text-white">Account info:</strong> name, email, username, profile information</li>
                  <li><strong className="text-white">Creator/church/charity info:</strong> organization name, profile details, public pages</li>
                  <li><strong className="text-white">User Content:</strong> videos, streams, comments, messages, thumbnails, captions, and related metadata</li>
                  <li><strong className="text-white">Support communications</strong></li>
                </ul>

                <h3 className="text-xl font-medium text-white mt-6 mb-3">b) Information collected automatically</h3>
                <ul className="list-disc list-inside text-gray-400 space-y-2">
                  <li><strong className="text-white">Device and usage data:</strong> IP address, device identifiers, app/browser type, pages viewed, watch history, clicks, and log data</li>
                  <li>Cookies and similar technologies (web), and similar identifiers in apps/TV apps (where applicable)</li>
                </ul>

                <h3 className="text-xl font-medium text-white mt-6 mb-3">c) Payment information</h3>
                <p className="text-gray-400 leading-relaxed">
                  We do not store full payment card details. Payments are processed by third-party processors. We may receive limited payment metadata such as billing status, transaction IDs, and timestamps.
                </p>

                <h3 className="text-xl font-medium text-white mt-6 mb-3">d) Location data</h3>
                <p className="text-gray-400 leading-relaxed">
                  We may infer general location (e.g., city/state) from IP address. We collect precise location only if you enable location permissions on your device and a feature requires it.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">2) How We Use Information</h2>
                <p className="text-gray-400 leading-relaxed">We use information to:</p>
                <ul className="list-disc list-inside text-gray-400 space-y-2 mt-2">
                  <li>Provide and operate the Service (accounts, streaming, features)</li>
                  <li>Process subscriptions and donations/tips, and prevent fraud</li>
                  <li>Personalize content and improve recommendations</li>
                  <li>Moderate content and enforce our policies</li>
                  <li>Communicate with you (support, updates, security notices)</li>
                  <li>Analyze performance and improve the Service</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">3) Cookies & Analytics</h2>
                <p className="text-gray-400 leading-relaxed">
                  We use cookies and basic analytics to keep you logged in, remember preferences, secure the Service, and understand usage. You can adjust browser settings to limit cookies, but some features may not work.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">4) How We Share Information</h2>
                <p className="text-gray-400 leading-relaxed">We may share information with:</p>
                <ul className="list-disc list-inside text-gray-400 space-y-2 mt-2">
                  <li><strong className="text-white">Service providers</strong> (hosting, analytics, customer support, communications)</li>
                  <li><strong className="text-white">Payment processors</strong> to complete transactions</li>
                  <li><strong className="text-white">Content delivery/infrastructure providers</strong> to stream content reliably</li>
                  <li><strong className="text-white">Legal/safety partners</strong> if required by law or to protect rights and security</li>
                  <li><strong className="text-white">Business transfers</strong> (merger, acquisition, or asset sale)</li>
                </ul>
                <p className="text-gray-400 leading-relaxed mt-4">
                  We do not sell your personal information in the ordinary sense.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">5) Public Information</h2>
                <p className="text-gray-400 leading-relaxed">
                  Some information may be public by default, such as your username, profile, public comments, and published videos/streams (if applicable). You can control certain settings where available.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">6) Data Retention</h2>
                <p className="text-gray-400 leading-relaxed">
                  We retain information as long as needed to provide the Service, comply with legal obligations, resolve disputes, and enforce our agreements. We may retain certain data to prevent fraud and handle legal requests.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">7) Security</h2>
                <p className="text-gray-400 leading-relaxed">
                  We use reasonable administrative, technical, and physical safeguards to protect information. However, no method of transmission or storage is completely secure.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">8) Children's Privacy</h2>
                <p className="text-gray-400 leading-relaxed">
                  The Service is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided personal information, contact <a href="mailto:info@gospeltube.tv" className="text-brand hover:underline">info@gospeltube.tv</a>.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">9) Your Choices and Rights</h2>
                <p className="text-gray-400 leading-relaxed">
                  Depending on your location, you may request access, correction, deletion, or a copy of your information, and object to certain processing. Contact: <a href="mailto:info@gospeltube.tv" className="text-brand hover:underline">info@gospeltube.tv</a>.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">10) International Users</h2>
                <p className="text-gray-400 leading-relaxed">
                  If you access the Service from outside the United States, your information may be processed in the United States or other locations where our providers operate.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">11) Changes to This Policy</h2>
                <p className="text-gray-400 leading-relaxed">
                  We may update this Privacy Policy from time to time and will update the "Last Updated" date.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">12) Contact</h2>
                <p className="text-gray-400 leading-relaxed">
                  Privacy questions or requests: <a href="mailto:info@gospeltube.tv" className="text-brand hover:underline">info@gospeltube.tv</a>.
                </p>
              </section>

            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} GospelTube Inc. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mt-4 text-sm">
            <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/privacy" className="text-brand">Privacy Policy</Link>
            <Link to="/content-policy" className="text-gray-400 hover:text-white transition-colors">Content Policy</Link>
            <Link to="/dmca" className="text-gray-400 hover:text-white transition-colors">Copyright/DMCA</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
